/**
 * Converts a URL search params string back to an object.
 * @param {string} params - The URL search params string.
 * @returns {Record<string, any>} - The deserialized object.
 */

// NOTE: Remove any and place more known type
function searchParamsToObject(params: string): Record<string, any> {
  const decodedParams = decodeURIComponent(params);
  const cleanedFromBrackets = decodedParams.replace(/^{|}$/g, '');

  const urlParams = new URLSearchParams(cleanedFromBrackets);
  const result: Record<string, any> = {};

  /**
   * Sets the value in the object based on the path.
   * @param {string} path - The dot-notated path for the value.
   * @param {any} value - The value to set.
   */
  function setValue(path: string, value: any) {
    const keys = path.split('.');
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        if (!current[key]) {
          current[key] = isNaN(Number(keys[index + 1])) ? {} : [];
        }
        current = current[key];
      }
    });
  }

  urlParams.forEach((value, key) => {
    setValue(key, value);
  });

  return result;
}

/**
 * Converts an object to URL search params.
 * @param {Record<string, any>} obj - The object to convert to search params.
 * @param {string} [prefix=''] - Optional prefix for nested objects.
 * @returns {string} - The serialized URL search params.
 */
function objectToSearchParams(obj: Record<string, any>, prefix = ''): string {
  const params = new URLSearchParams();

  function processObject(data: Record<string, any>, parentKey: string) {
    Object.keys(data).forEach((key) => {
      const value = data[key];
      const fullKey = parentKey ? `${parentKey}.${key}` : key;

      if (typeof value === 'object' && !Array.isArray(value)) {
        // Recursive call for nested objects
        processObject(value, fullKey);
      } else if (Array.isArray(value)) {
        // Handling arrays
        value.forEach((item, index) => {
          if (typeof item === 'object') {
            processObject(item, `${fullKey}.${index}`);
          } else {
            params.append(`${fullKey}.${index}`, item);
          }
        });
      } else {
        // Add regular key-value pairs
        params.append(fullKey, value);
      }
    });
  }

  processObject(obj, prefix);
  return params.toString();
}

export { searchParamsToObject, objectToSearchParams };
